import { useState } from 'react'
import { Ghost, Home2, Moon, Sun1 } from 'iconsax-react'

// desc 暂留空(沿用现状),后面补充;结构先留好
const PERSONAS = [
    { id: 'patient', label: '耐心', emoji: '🌿', desc: '' },
    { id: 'lively', label: '活泼', emoji: '✨', desc: '' }
]

// 气泡正文字号三档(px);中档=默认。值同步到 MessageList 的 fontSize
const FONT_SIZES = [
    { label: '小', px: 14 },
    { label: '中', px: 16 },
    { label: '大', px: 18 }
]

// iconsax 无"裸双 chevron / 裸对勾",内联画最干净可控
function ChevronExpand() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="text-text-secondary">
            <path d="M7 9.5l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17 14.5l-5 5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
function ChevronUp() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden className="text-text-secondary">
            <path d="M5 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}
function CheckMark() {
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden className="shrink-0 text-accent">
            <path d="M5 12.5l4.4 4.4L19 7.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

export function ChatHeader({
    theme,
    onChangeTheme,
    persona,
    onChangePersona,
    fontSize,
    onChangeFontSize,
    blurAi,
    onToggleBlurAi,
    onBack
}: {
    theme: 'light' | 'dark'
    onChangeTheme: (next: 'light' | 'dark') => void
    persona: string
    onChangePersona: (next: string) => void
    fontSize: number
    onChangeFontSize: (px: number) => void
    blurAi: boolean
    onToggleBlurAi: () => void
    onBack: () => void
}) {
    const [open, setOpen] = useState(false)
    const [sizeOpen, setSizeOpen] = useState(false)
    const personaLabel = PERSONAS.find((p) => p.id === persona)?.label ?? persona

    return (
        <header className="hairline-b relative z-40 flex h-14 items-center justify-between px-4">
            <button
                onClick={onBack}
                aria-label="回到首页"
                className="-ml-1.5 flex h-10 w-10 items-center justify-center text-text-secondary/85 transition-colors hover:text-text-primary active:opacity-50"
            >
                <Home2 size={26} color="currentColor" variant="Linear" />
            </button>

            {/* persona 绝对居中,不被右侧按钮数量推偏 */}
            <div className="absolute left-1/2 -translate-x-1/2">
                <button
                    onClick={() => setOpen((o) => !o)}
                    aria-label="切换角色"
                    className="glass flex items-center gap-1.5 rounded-full bg-bg-secondary py-1 pl-3.5 pr-2.5 text-sm font-medium text-text-primary transition active:opacity-70"
                >
                    {open ? '收起' : personaLabel}
                    {open ? <ChevronUp /> : <ChevronExpand />}
                </button>
            </div>

            <div className="flex items-center gap-0.5">
                {/* 一键模糊 AI 文字(只听语音不看字):单图标,开启时染强调色表示激活;仅本机 */}
                <button
                    onClick={onToggleBlurAi}
                    aria-label={blurAi ? '显示AI文字' : '模糊AI文字'}
                    className={`flex h-10 w-10 items-center justify-center transition-colors active:opacity-50 ${
                        blurAi ? 'text-accent' : 'text-text-secondary/85 hover:text-text-primary'
                    }`}
                >
                    <Ghost size={26} color="currentColor" variant="Linear" />
                </button>

                {/* 字号调节:Aa 图标 → 紧凑弹窗(背后聊天可见,实时看效果) */}
                <div className="relative">
                    <button
                        onClick={() => setSizeOpen((o) => !o)}
                        aria-label="字体大小"
                        className="flex h-10 w-10 items-center justify-center text-text-secondary/85 transition-colors hover:text-text-primary active:opacity-50"
                    >
                        <span className="flex items-baseline font-display font-semibold leading-none">
                            <span className="text-[23px]">A</span>
                            <span className="text-[15px]">A</span>
                        </span>
                    </button>
                    {sizeOpen && (
                        <>
                            {/* 透明点击层:点空白收起,不压暗聊天(要能直接看字号变化) */}
                            <div className="fixed inset-0 z-30" onClick={() => setSizeOpen(false)} />
                            <div className="glass animate-dropdown absolute right-0 top-12 z-40 flex gap-1 rounded-2xl bg-bg-secondary p-1.5">
                                {FONT_SIZES.map((s) => {
                                    const sel = fontSize === s.px
                                    return (
                                        <button
                                            key={s.px}
                                            onClick={() => onChangeFontSize(s.px)}
                                            aria-label={`字号${s.label}`}
                                            className={`flex h-9 w-9 items-center justify-center rounded-xl transition ${
                                                sel
                                                    ? 'bg-accent/12 text-accent ring-1 ring-accent/30'
                                                    : 'text-text-primary active:bg-accent/8'
                                            }`}
                                        >
                                            <span className="font-display font-semibold leading-none" style={{ fontSize: s.px }}>
                                                A
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>

                <button
                    onClick={() => onChangeTheme(theme === 'dark' ? 'light' : 'dark')}
                    aria-label="切换主题"
                    className="-mr-1.5 flex h-10 w-10 items-center justify-center text-text-secondary/85 transition-colors hover:text-text-primary active:opacity-50"
                >
                    {theme === 'dark' ? (
                        <Sun1 size={26} color="currentColor" variant="Linear" />
                    ) : (
                        <Moon size={26} color="currentColor" variant="Linear" />
                    )}
                </button>
            </div>

            {open && (
                <>
                    {/* 压暗 + 磨砂聊天区;点击空白收起。top-14 让出 header 自身(收起按钮仍可点) */}
                    <div
                        className="animate-fade fixed inset-x-0 bottom-0 top-14 z-30 bg-bg/55 backdrop-blur-md"
                        onClick={() => setOpen(false)}
                    />
                    <div className="fixed inset-x-0 top-14 z-40 px-3 pt-2">
                        <div className="glass animate-dropdown mx-auto max-w-lg rounded-3xl bg-bg-secondary p-2">
                            {PERSONAS.map((p) => {
                                const sel = p.id === persona
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => {
                                            onChangePersona(p.id)
                                            setOpen(false)
                                        }}
                                        className={`flex w-full items-center gap-3.5 rounded-2xl px-3 py-3 text-left transition ${
                                            sel
                                                ? 'bg-accent/12 shadow-lg shadow-accent/20 ring-1 ring-accent/30'
                                                : 'active:bg-accent/8'
                                        }`}
                                    >
                                        <span className="glass flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-xl">
                                            {p.emoji}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-[16px] font-semibold text-text-primary">
                                                {p.label}
                                            </span>
                                            {p.desc && (
                                                <span className="mt-0.5 block truncate text-[13px] text-text-secondary">
                                                    {p.desc}
                                                </span>
                                            )}
                                        </span>
                                        {sel && <CheckMark />}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </>
            )}
        </header>
    )
}
